import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeftRight, Loader2, Check, X, Ban } from "lucide-react";

const GIFTS = [
  { key: "rose",   emoji: "🌹", label: "Rose",   coins: 1 },
  { key: "fire",   emoji: "🔥", label: "Fire",   coins: 5 },
  { key: "kiss",   emoji: "💋", label: "Kiss",   coins: 10 },
  { key: "bottle", emoji: "🍾", label: "Bottle", coins: 50 },
];

function GiftSelect({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid={testid}>
      {GIFTS.map((g) => (
        <button
          key={g.key}
          type="button"
          onClick={() => onChange(g.key)}
          className={`flex flex-col items-center py-2 border rounded-lg ${
            value === g.key ? "bg-rose-600/20 border-rose-500" : "bg-black/30 border-white/10 hover:border-white/25"
          }`}
        >
          <span className="text-2xl leading-none">{g.emoji}</span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{g.label}</span>
          <span className="text-[10px] text-amber-400">{g.coins}c</span>
        </button>
      ))}
    </div>
  );
}

export default function GiftTradesPage() {
  const [box, setBox] = useState("inbox");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ to_handle: "", i_give_key: "rose", i_want_key: "fire", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gift-trades", { params: { box } });
      setRows(data);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [box]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.to_handle.trim()) return toast.error("Enter a recipient handle.");
    setBusy(true);
    try {
      await api.post("/gift-trades", { ...form, to_handle: form.to_handle.replace(/^@/, "").trim() });
      toast.success("Trade proposed!");
      setShowForm(false);
      setForm({ to_handle: "", i_give_key: "rose", i_want_key: "fire", note: "" });
      setBox("sent"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not propose trade.");
    } finally { setBusy(false); }
  };

  const respond = async (id, action) => {
    try {
      await api.post(`/gift-trades/${id}/respond`, { action });
      toast.success(action === "accept" ? "Trade accepted!" : "Trade declined.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not respond."); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this trade? Your escrowed coins will be refunded.")) return;
    try {
      const { data } = await api.post(`/gift-trades/${id}/cancel`);
      toast.success(`Trade cancelled — ${data.refunded_coins} coins refunded.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel."); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="gift-trades-root">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-1">Trades</div>
          <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
            <ArrowLeftRight className="text-rose-500" size={28} /> Gift Trades
          </h1>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} className="btn-crimson rounded-full h-9 px-4" data-testid="trade-new-toggle">
          {showForm ? "Cancel" : "New trade"}
        </Button>
      </header>

      {showForm && (
        <div className="card-surface p-5 mb-5 space-y-3" data-testid="trade-form">
          <label className="text-xs uppercase tracking-widest text-zinc-500">To @handle</label>
          <Input value={form.to_handle} onChange={(e) => setForm((f) => ({ ...f, to_handle: e.target.value }))} placeholder="e.g. leyamazing" className="bg-black/40 border-white/10 rounded-none" data-testid="trade-to-handle" />
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">You give</div>
            <GiftSelect value={form.i_give_key} onChange={(v) => setForm((f) => ({ ...f, i_give_key: v }))} testid="trade-give" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">You want</div>
            <GiftSelect value={form.i_want_key} onChange={(v) => setForm((f) => ({ ...f, i_want_key: v }))} testid="trade-want" />
          </div>
          <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Add a note (optional)" className="bg-black/40 border-white/10 rounded-none" data-testid="trade-note" />
          <Button onClick={submit} disabled={busy} className="btn-crimson rounded-full w-full" data-testid="trade-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Propose trade
          </Button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["inbox", "sent"].map((b) => (
          <button
            key={b}
            onClick={() => setBox(b)}
            data-testid={`trade-tab-${b}`}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              box === b ? "bg-rose-600 border-rose-600 text-white" : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {b === "inbox" ? "Inbox" : "Sent"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">No trades in your {box} yet.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="card-surface p-4" data-testid={`trade-row-${r.id}`}>
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10 border border-white/10">
                  <AvatarImage src={r.other?.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-sm">{r.other?.display_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.other?.display_name}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                    <span>{r.give_gift?.emoji} {r.give_gift?.label} ({r.give_gift?.coins}c)</span>
                    <ArrowLeftRight size={10} />
                    <span>{r.want_gift?.emoji} {r.want_gift?.label} ({r.want_gift?.coins}c)</span>
                  </div>
                  {r.note && <div className="text-xs text-zinc-400 mt-1">"{r.note}"</div>}
                </div>
                <div className="shrink-0">
                  {r.status === "pending" && box === "inbox" ? (
                    <div className="flex gap-2">
                      <Button onClick={() => respond(r.id, "accept")} className="btn-crimson rounded-full h-8 px-3 text-xs" data-testid={`trade-accept-${r.id}`}><Check size={12} /></Button>
                      <Button onClick={() => respond(r.id, "decline")} variant="ghost" className="rounded-full h-8 px-3 text-xs border border-white/15" data-testid={`trade-decline-${r.id}`}><X size={12} /></Button>
                    </div>
                  ) : r.status === "pending" && box === "sent" ? (
                    <Button onClick={() => cancel(r.id)} variant="ghost" className="rounded-full h-8 px-3 text-xs border border-white/15 text-zinc-300 hover:text-white" data-testid={`trade-cancel-${r.id}`}>
                      <Ban size={12} className="mr-1" /> Cancel
                    </Button>
                  ) : (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                      r.status === "accepted" ? "bg-emerald-500/20 text-emerald-300" :
                      r.status === "declined" ? "bg-zinc-500/20 text-zinc-400" :
                      r.status === "cancelled" ? "bg-zinc-500/20 text-zinc-400" :
                      "bg-amber-500/20 text-amber-300"
                    }`}>{r.status}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
